import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useTheme, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { formatNZDate, formatNZDateTime, getNZNow } from "../utils/timezone";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import ReceiptDialog from "../components/ReceiptDialog";
import SEO from "../components/SEO";
import ImpersonationBanner from "../components/ImpersonationBanner";
import { AIRecommendationNudge } from "./customer/AIRecommendationNudge";
import { 
  Sun, Moon, LogOut, Plus, Clock, Zap, MapPin, Star, 
  ChevronRight, DollarSign, Calendar, Store, FileText,
  CheckCircle, AlertCircle, Package, Eye, Edit, Lock, Car, CreditCard, Receipt, Trash2, ExternalLink, Settings, Timer
} from "lucide-react";
import { CustomerQuotesDialog } from "./customer/CustomerQuotesDialog";
import { CustomerViewRequestDialog } from "./customer/CustomerViewRequestDialog";

// Countdown timer component
function ExpiryCountdown({ expiryTime }) {
  const [timeLeft, setTimeLeft] = useState("");
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!expiryTime) return;
    
    const updateCountdown = () => {
      const now = getNZNow();
      const expiry = new Date(expiryTime);
      // Quote expiry dates (date-only strings) should expire at end of day
      if (typeof expiryTime === "string" && /^\d{4}-\d{2}-\d{2}$/.test(expiryTime)) {
        expiry.setHours(23, 59, 59, 999);
      }
      const diff = expiry - now;
      
      if (diff <= 0) {
        setIsExpired(true);
        setTimeLeft("Expired");
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h remaining`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m remaining`);
      } else {
        setTimeLeft(`${minutes}m remaining`);
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [expiryTime]);

  if (!expiryTime) return null;
  
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${isExpired ? "text-red-500" : "text-amber-600"}`} data-testid="expiry-countdown">
      <Timer className="h-3 w-3" />
      {timeLeft}
    </span>
  );
}

function isQuoteExpired(quote, request) {
  // Check quote-level expiry (expires at END of the expiry day in NZ time)
  if (quote.quote_expiry) {
    const expiry = new Date(quote.quote_expiry);
    expiry.setHours(23, 59, 59, 999);
    if (getNZNow() > expiry) return true;
  }
  // Check request-level expiry
  if (request?.expiry_time) {
    const expiry = new Date(request.expiry_time);
    if (getNZNow() > expiry) return true;
  }
  return false;
}

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";
const LIGHT_LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";

const SERVICE_TYPE_LABELS = {
  tyres_only: "Tyres",
  tyres_alignment: "Tyres + Alignment",
  tyres_rotation: "Tyres + Rotation",
  tyres_alignment_rotation: "Tyres + Alignment + Rotation",
  alignment_only: "Wheel Alignment",
  alignment_rotation: "Alignment + Rotation",
  rotation_only: "Tyre Rotation"
};

// Helper function to get service label from selected_services array
const getServiceLabel = (request) => {
  // Build label from selected_services for accuracy (priority over service_type)
  const services = request.selected_services || [];
  if (services.length > 0) {
    const parts = [];
    if (services.includes("tyres")) parts.push("Tyres");
    if (services.includes("alignment")) parts.push("Alignment");
    if (services.includes("rotation")) parts.push("Rotation");
    if (parts.length > 0) return parts.join(" + ");
  }
  
  // Fall back to service_type map
  if (request.service_type && SERVICE_TYPE_LABELS[request.service_type]) {
    return SERVICE_TYPE_LABELS[request.service_type];
  }
  
  return "Service";
};

const PRICE_RANGE_LABELS = {
  lower: "Budget Friendly",
  mid: "Mid Range",
  luxury: "Premium",
  winter: "Winter Tyres",
  all_season: "All Season",
  ev: "EV Tyres"
};

const STATUS_COLORS = {
  active: "bg-green-500/10 text-green-600 border-green-500/30",
  accepted: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  completed: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  expired: "bg-gray-500/10 text-gray-600 border-gray-500/30",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
  paid: "bg-green-500/10 text-green-600 border-green-500/30",
  confirmed: "bg-blue-500/10 text-blue-600 border-blue-500/30"
};

// Check if request is editable (no quotes received yet and not expired)
const isEditable = (request) => {
  if (!request) return false;
  if (request.quotes_received > 0) return false;
  if (["quoted", "ordered", "completed", "accepted", "closed", "expired"].includes(request.request_status)) return false;
  return true;
};

// Get reason why locked
const getLockReason = (request) => {
  if (!request) return null;
  if (request.request_status === "expired") return "This request has expired.";
  if (request.quotes_received > 0) return "Locked: a shop has submitted a quote.";
  if (["quoted", "ordered", "completed", "accepted", "closed"].includes(request.request_status)) return "This request can no longer be edited.";
  return null;
};

// Edit time remaining - no longer time-based
const getEditTimeRemaining = () => null;

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [autoOpenAi, setAutoOpenAi] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: 5, comments: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [viewRequestDialogOpen, setViewRequestDialogOpen] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedRequest, setEditedRequest] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmingService, setConfirmingService] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptOrderId, setReceiptOrderId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRequestId, setDeleteRequestId] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [requestsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/customer/quote-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/customer/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setRequests(requestsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchQuotes = async (requestId) => {
    setQuotesLoading(true);
    try {
      const response = await axios.get(`${API}/customer/quotes/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      toast.error("Failed to load quotes");
    } finally {
      setQuotesLoading(false);
    }
  };

  const handleViewQuotes = (request) => {
    setSelectedRequest(request);
    fetchQuotes(request.request_id);
  };

  const handleViewRequest = (request) => {
    setViewingRequest(request);
    setEditedRequest({
      brand_preference: request.brand_preference || "",
      price_range: request.price_range || "mid",
      comparison_intent: request.comparison_intent || "",
      tyre_brand: request.tyre_brand || "",
      tyre_model: request.tyre_model || "",
      driving_priority: request.driving_priority || "",
      search_radius: request.search_radius || 30,
      quantity: request.quantity || 4,
      customer_note: request.customer_note || "",
      vehicle_info: request.vehicle_info || {},
      tyre_specs: request.tyre_specs || {},
    });
    setEditMode(false);
    setViewRequestDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!viewingRequest || !editedRequest) return;
    
    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/customer/quote-request/${viewingRequest.request_id}`,
        editedRequest,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Request updated successfully");
      setEditMode(false);
      fetchData(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update request");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAcceptQuote = async (quote) => {
    try {
      const response = await axios.post(
        `${API}/customer/accept-quote`,
        { quote_id: quote.quote_id },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(response.data.message || "Quote accepted! Please contact the shop to book your appointment.");
      setSelectedRequest(null);
      setQuotes([]);
      fetchData(); // Refresh the dashboard
    } catch (error) {
      // Check if the error is because the quote was already accepted
      const errorDetail = error.response?.data?.detail || "";
      if (errorDetail.includes("already accepted") || errorDetail.includes("Another quote")) {
        // Quote was already accepted - just refresh to show current state
        toast.info("Quote already accepted. Refreshing...");
        setSelectedRequest(null);
        setQuotes([]);
        fetchData();
      } else {
        toast.error(errorDetail || "Failed to accept quote");
      }
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewOrder) return;
    
    setSubmittingReview(true);
    try {
      await axios.post(
        `${API}/customer/review`,
        {
          order_id: reviewOrder.order_id,
          rating: reviewData.rating,
          comments: reviewData.comments || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success("Review submitted! You earned credits.");
      setReviewDialogOpen(false);
      setReviewOrder(null);
      setReviewData({ rating: 5, comments: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleConfirmService = async (orderId) => {
    setConfirmingService(true);
    try {
      // Step 1: Confirm the service
      await axios.post(
        `${API}/customer/confirm-service/${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Service confirmed! Redirecting to payment...");
      
      // Step 2: Create Stripe checkout session
      const checkoutResponse = await axios.post(
        `${API}/payments/create-checkout?order_id=${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Step 3: Redirect to Stripe checkout
      if (checkoutResponse.data.checkout_url) {
        window.location.href = checkoutResponse.data.checkout_url;
      } else {
        toast.error("Failed to create payment session");
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process payment");
      fetchData();
    } finally {
      setConfirmingService(false);
    }
  };

  // Handle direct payment for orders already in awaiting_customer_payment status
  const handlePayNow = async (orderId) => {
    setConfirmingService(true);
    try {
      const checkoutResponse = await axios.post(
        `${API}/payments/create-checkout?order_id=${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (checkoutResponse.data.checkout_url) {
        window.location.href = checkoutResponse.data.checkout_url;
      } else {
        toast.error("Failed to create payment session");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setConfirmingService(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!deleteReason.trim()) {
      toast.error("Please provide a reason for deletion");
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/customer/quote-request/${deleteRequestId}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { reason: deleteReason }
      });
      toast.success("Request deleted successfully");
      setDeleteDialogOpen(false);
      setDeleteRequestId(null);
      setDeleteReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete request");
    } finally {
      setDeleting(false);
    }
  };

  const formatTimeRemaining = (expiryTime) => {
    const expiry = new Date(expiryTime);
    const now = getNZNow();
    const diff = expiry - now;
    
    if (diff <= 0) return "Expired";
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    
    if (days > 0) return `${days}d ${remainingHours}h remaining`;
    return `${hours}h remaining`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="customer-dashboard">
      <SEO page="customerDashboard" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <img src={theme === "dark" ? LOGO_URL : LIGHT_LOGO_URL} alt="TyreMatch" className="h-10" />
            </Link>

            <div className="flex items-center gap-4">
              <ImpersonationBanner />
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle"
              >
                {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>

              <Link to="/customer/account">
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  data-testid="account-btn"
                >
                  <Settings className="h-5 w-5" />
                </Button>
              </Link>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="rounded-full text-destructive"
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-1">
              Welcome back, {user?.name?.split(" ")[0]}!
            </h1>
            <p className="text-muted-foreground">
              Manage your quote requests and orders
            </p>
          </div>

          <Button
            onClick={() => navigate("/customer/quote-request")}
            className="bg-primary text-primary-foreground"
            data-testid="new-request-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Quote Request
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="glass-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{requests.length}</p>
                <p className="text-sm text-muted-foreground">Total Requests</p>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                <Package className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{orders.length}</p>
                <p className="text-sm text-muted-foreground">Orders</p>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">${user?.credits_balance?.toFixed(2) || "0.00"}</p>
                <p className="text-sm text-muted-foreground">Credits Balance</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="requests" className="space-y-6">
          <TabsList>
            <TabsTrigger value="requests" data-testid="requests-tab">
              My Requests ({requests.length})
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab" className="relative">
              Orders ({orders.length})
              {orders.filter(o => o.order_status === "confirmed" || o.order_status === "pending_service").length > 0 && (
                <span className="ml-2 flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-green-500 text-white text-xs font-medium px-1 animate-pulse">
                  {orders.filter(o => o.order_status === "confirmed" || o.order_status === "pending_service").length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Requests Tab */}
          <TabsContent value="requests" className="space-y-4">
            {requests.length === 0 ? (
              <Card className="text-center p-12">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">No requests yet</h3>
                <p className="text-muted-foreground mb-4">
                  Get started by creating your first quote request
                </p>
                <Button
                  onClick={() => navigate("/customer/quote-request")}
                  className="bg-primary text-primary-foreground"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Request
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {requests.map((request) => (
                  <Card key={request.request_id} className="card-hover" data-testid={`request-${request.request_id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <h3 className="font-heading text-xl font-semibold">
                              {request.request_number || `#${request.request_id}`}
                            </h3>
                            <Badge className={STATUS_COLORS[request.request_status]}>
                              {request.request_status}
                            </Badge>
                            {request.request_type === "urgent" && (
                              <Badge className="bg-primary/10 text-primary border-primary/30">
                                <Zap className="h-3 w-3 mr-1" />
                                Urgent
                              </Badge>
                            )}
                            {request.comparison_intent === "specific" && (
                              <Badge
                                className="bg-blue-500/10 text-blue-600 border-blue-500/30 dark:text-blue-400"
                                title="You asked shops to quote a specific tyre — quotes will be directly comparable."
                                data-testid={`intent-badge-${request.request_id}`}
                              >
                                Exact Match Comparison
                              </Badge>
                            )}
                            {request.comparison_intent === "budget" && (
                              <Badge
                                className="bg-purple-500/10 text-purple-600 border-purple-500/30 dark:text-purple-400"
                                title="Shops will recommend tyres within your budget and driving priorities."
                                data-testid={`intent-badge-${request.request_id}`}
                              >
                                Budget Match
                              </Badge>
                            )}
                            {request.comparison_intent === "open" && (
                              <Badge
                                className="bg-amber-500/10 text-amber-600 border-amber-500/30 dark:text-amber-400"
                                title="Open to suggestions — quotes may vary significantly in brand and price."
                                data-testid={`intent-badge-${request.request_id}`}
                              >
                                Open to Suggestions
                              </Badge>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Package className="h-4 w-4" />
                              {getServiceLabel(request)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {typeof request.address === 'object' 
                                ? (request.address?.city || request.address?.suburb || request.address?.postcode || 'NZ')
                                : request.postcode || request.address || 'NZ'
                              } ({request.search_radius} km)
                            </span>
                            {(request.rego || request.vehicle_info?.rego) && (
                              <span className="flex items-center gap-1">
                                <Car className="h-4 w-4" />
                                {request.rego || request.vehicle_info?.rego}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                          <div className="text-right">
                            <div className="flex items-center gap-2">
                              <Eye className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{request.quotes_received} quotes</span>
                            </div>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTimeRemaining(request.expiry_time)}
                            </p>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              onClick={() => handleViewRequest(request)}
                              data-testid={`view-request-${request.request_id}`}
                            >
                              {isEditable(request) ? (
                                <>
                                  <Edit className="h-4 w-4 mr-2" />
                                  View/Edit
                                </>
                              ) : (
                                <>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </>
                              )}
                            </Button>
                            <Button
                              onClick={() => handleViewQuotes(request)}
                              disabled={request.quotes_received === 0}
                              data-testid={`view-quotes-${request.request_id}`}
                            >
                              View Quotes
                              <ChevronRight className="h-4 w-4 ml-2" />
                            </Button>
                            {/* Delete button - only for active/expired requests */}
                            {request.request_status !== "accepted" && request.request_status !== "completed" && (
                              <Button
                                variant="outline"
                                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                onClick={() => {
                                  setDeleteRequestId(request.request_id);
                                  setDeleteDialogOpen(true);
                                }}
                                data-testid={`delete-request-${request.request_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            {orders.length === 0 ? (
              <Card className="text-center p-12">
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">No orders yet</h3>
                <p className="text-muted-foreground">
                  Accept a quote to create your first order
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => {
                  // Determine order status display
                  const getOrderStatusInfo = () => {
                    switch (order.order_status) {
                      case "confirmed":
                      case "pending_service":
                        return {
                          label: "Confirmed",
                          color: "bg-green-500/10 text-green-600 border-green-500/30",
                          description: "Your order is confirmed. Please contact the shop to book your appointment.",
                          showAction: false,
                          showShopContact: true
                        };
                      case "awaiting_shop_availability_confirmation":
                        return {
                          label: "Awaiting Shop Confirmation",
                          color: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
                          description: "The shop is checking availability and will confirm shortly.",
                          showAction: false,
                          showShopContact: false
                        };
                      case "completed":
                        return {
                          label: "Completed",
                          color: "bg-blue-500/10 text-blue-600 border-blue-500/30",
                          description: "Service completed. Thank you for your business!",
                          showAction: false,
                          showShopContact: false
                        };
                      case "cancelled":
                        return {
                          label: "Cancelled",
                          color: "bg-red-500/10 text-red-600 border-red-500/30",
                          description: "This order was cancelled.",
                          showAction: false,
                          showShopContact: false
                        };
                      default:
                        return {
                          label: order.order_status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Unknown",
                          color: "bg-gray-500/10 text-gray-600 border-gray-500/30",
                          description: "",
                          showAction: false,
                          showShopContact: false
                        };
                    }
                  };

                  const statusInfo = getOrderStatusInfo();

                  return (
                    <Card 
                      key={order.order_id} 
                      className={`card-hover ${
                        (order.order_status === "confirmed" || order.order_status === "pending_service")
                          ? "border-green-500/50 bg-green-500/5" 
                          : ""
                      }`}
                      data-testid={`order-${order.order_id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex flex-col gap-4">
                          {/* Order Header */}
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="space-y-2">
                              <div className="flex items-center gap-3 flex-wrap">
                                <h3 className="font-heading text-xl font-semibold">
                                  Order #{order.order_id.substring(0, 8)}
                                </h3>
                                <Badge className={statusInfo.color}>
                                  {statusInfo.label}
                                </Badge>
                              </div>

                              <p className="text-sm text-muted-foreground">
                                Created on {formatNZDate(order.created_at)}
                              </p>
                            </div>

                            <div className="text-right">
                              <p className="text-2xl font-heading font-bold text-primary">
                                ${(order.order_amount || 0).toFixed(2)}
                              </p>
                              <p className="text-xs text-muted-foreground">incl. GST</p>
                            </div>
                          </div>

                          {/* Status Description */}
                          {statusInfo.description && (
                            <div className={`p-3 rounded-lg ${
                              (order.order_status === "confirmed" || order.order_status === "pending_service")
                                ? "bg-green-500/10 border border-green-500/30" 
                                : "bg-muted/30"
                            }`}>
                              <div className="flex items-start gap-3">
                                {(order.order_status === "confirmed" || order.order_status === "pending_service") && (
                                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                                )}
                                {order.order_status === "awaiting_shop_availability_confirmation" && (
                                  <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
                                )}
                                {order.order_status === "completed" && (
                                  <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                                )}
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{statusInfo.description}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Shop Contact Details - for confirmed orders */}
                          {statusInfo.showShopContact && order.shop_info && (
                            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                              <h4 className="font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-2 mb-3">
                                <Store className="h-4 w-4" />
                                Contact Shop to Book Appointment
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="text-muted-foreground">Shop Name</p>
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">{order.shop_info.shop_name || "N/A"}</p>
                                    {order.shop_info.google_rating != null && (
                                      <div className="flex items-center gap-1 text-xs bg-muted/50 px-1.5 py-0.5 rounded-full">
                                        <svg viewBox="0 0 24 24" className="h-3 w-3 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                        </svg>
                                        <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                                        <span className="font-medium">{order.shop_info.google_rating.toFixed(1)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Phone</p>
                                  <a href={`tel:${order.shop_info.phone}`} className="font-medium text-primary hover:underline">
                                    {order.shop_info.phone || "N/A"}
                                  </a>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Email</p>
                                  <a href={`mailto:${order.shop_info.email}`} className="font-medium text-primary hover:underline">
                                    {order.shop_info.email || "N/A"}
                                  </a>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Address</p>
                                  <p className="font-medium">{order.shop_info.address?.full_address || "N/A"}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2 justify-end">
                            {/* Leave Review button for completed orders */}
                            {order.order_status === "completed" && (
                              <>
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setReceiptOrderId(order.order_id);
                                    setReceiptDialogOpen(true);
                                  }}
                                  data-testid={`view-receipt-${order.order_id}`}
                                >
                                  <Receipt className="h-4 w-4 mr-2" />
                                  View Confirmation
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setReviewOrder(order);
                                    setReviewDialogOpen(true);
                                  }}
                                  data-testid={`review-order-${order.order_id}`}
                                >
                                  <Star className="h-4 w-4 mr-2" />
                                  Leave Review
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Quotes Dialog */}

      {/* AI Recommendation nudge — pops up once per request (7-day TTL)
          when the customer has multiple quotes waiting. */}
      <AIRecommendationNudge
        requests={requests}
        onOpenQuotes={(request, opts) => {
          setSelectedRequest(request);
          fetchQuotes(request.request_id);
          if (opts?.openAi) setAutoOpenAi(true);
        }}
      />

      {/* Quotes Comparison Dialog */}
      <CustomerQuotesDialog
        selectedRequest={selectedRequest}
        setSelectedRequest={setSelectedRequest}
        quotes={quotes}
        quotesLoading={quotesLoading}
        onAcceptQuote={handleAcceptQuote}
        autoOpenAi={autoOpenAi}
        onAutoOpenAiConsumed={() => setAutoOpenAi(false)}
      />

      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Leave a Review</DialogTitle>
            <DialogDescription>
              Share your experience and earn credits
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rating</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <Button
                    key={rating}
                    variant={reviewData.rating >= rating ? "default" : "outline"}
                    size="icon"
                    onClick={() => setReviewData({ ...reviewData, rating })}
                    className={reviewData.rating >= rating ? "bg-yellow-500 hover:bg-yellow-600" : ""}
                    data-testid={`rating-${rating}`}
                  >
                    <Star className={`h-4 w-4 ${reviewData.rating >= rating ? "fill-white" : ""}`} />
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comments">Comments (Optional - earn extra credits!)</Label>
              <Textarea
                id="comments"
                placeholder="Tell us about your experience..."
                value={reviewData.comments}
                onChange={(e) => setReviewData({ ...reviewData, comments: e.target.value })}
                rows={4}
                data-testid="review-comments"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitReview}
                disabled={submittingReview}
                className="bg-primary text-primary-foreground"
                data-testid="submit-review-btn"
              >
                {submittingReview ? "Submitting..." : "Submit Review"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View/Edit Request Dialog */}

      {/* View/Edit Request + Delete Dialogs */}
      <CustomerViewRequestDialog
        viewRequestDialogOpen={viewRequestDialogOpen}
        setViewRequestDialogOpen={setViewRequestDialogOpen}
        viewingRequest={viewingRequest}
        setViewingRequest={setViewingRequest}
        editMode={editMode}
        setEditMode={setEditMode}
        editedRequest={editedRequest}
        setEditedRequest={setEditedRequest}
        savingEdit={savingEdit}
        handleSaveEdit={handleSaveEdit}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        deleteRequestId={deleteRequestId}
        setDeleteRequestId={setDeleteRequestId}
        deleteReason={deleteReason}
        setDeleteReason={setDeleteReason}
        deleting={deleting}
        handleDeleteRequest={handleDeleteRequest}
        handleViewQuotes={handleViewQuotes}
      />
    </div>
  );
}
